import React from "react";
import { Box, CircularProgress, IconButton, ListItemIcon, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useLocation } from "react-router-dom";
import SECTION_INFO from "./sectionInfo.json";

/**
 * Coach workspace — shared look (design PDF "BMS COACH EXPERIENCE").
 * New screens only; existing pages are untouched.
 */

export const CW = {
  primary: "var(--portal-theme-primary, #0b9fae)",
  ink: "#1f2a33",
  muted: "#66737f",
  line: "#e1e7ec",
  soft: "#f4f7f9",
  tealSoft: "#e3f5f7",
  red: "#c62828",
  redSoft: "#fdecea",
  amber: "#b45309",
  amberSoft: "#fdf1dc",
  green: "#2e7d32",
  greenSoft: "#e6f4ea",
};

export const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
export const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Section info icons (3 Nov 2026). Shown ONLY while the current URL has
 * ?mode=info; any URL without it shows nothing. Text per section lives in
 * ./sectionInfo.json — edit the JSON to change what the hover shows.
 */
const OLD_INFO_KEY = "cw_info_mode"; // earlier build kept the mode per tab

export const useInfoMode = () => {
  const location = useLocation();
  const fromRouter = new URLSearchParams(location?.search || "").get("mode");
  const fromWindow = typeof window !== "undefined" ? new URLSearchParams(window.location.search || "").get("mode") : null;
  try {
    window.sessionStorage.removeItem(OLD_INFO_KEY);
  } catch (_err) {
    /* storage blocked — nothing to clear */
  }
  return (fromRouter || fromWindow) === "info";
};

function InfoBlock({ label, children }) {
  if (!children || (Array.isArray(children) && !children.length)) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, color: "#8fd8e0", textTransform: "uppercase" }}>{label}</Typography>
      {Array.isArray(children) ? (
        <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
          {children.map((line, i) => (
            <li key={i}>
              <Typography sx={{ fontSize: 12.5, color: "#fff", lineHeight: 1.5 }}>{line}</Typography>
            </li>
          ))}
        </Box>
      ) : (
        <Typography sx={{ fontSize: 12.5, color: "#fff", lineHeight: 1.5 }}>{children}</Typography>
      )}
    </Box>
  );
}

/** "i" icon; hover / focus shows where, what, logic and why for one section. */
export function InfoHint({ id, sx }) {
  const on = useInfoMode();
  const info = SECTION_INFO[id];
  if (!on || !info) return null;
  return (
    <Tooltip
      arrow
      placement="bottom-start"
      enterDelay={80}
      leaveDelay={120}
      componentsProps={{ tooltip: { sx: { bgcolor: "#1f2a33", maxWidth: 520, maxHeight: "75vh", overflowY: "auto", p: 1.75, borderRadius: "10px" } }, arrow: { sx: { color: "#1f2a33" } } }}
      title={
        <Box data-testid={`info-tip-${id}`}>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#fff" }}>{info.title}</Typography>
          <InfoBlock label="Kahan">{info.kahan}</InfoBlock>
          <InfoBlock label="Kya dikhta hai">{info.kya}</InfoBlock>
          <InfoBlock label="Logic">{info.logic}</InfoBlock>
          <InfoBlock label="Kyun">{info.kyun}</InfoBlock>
        </Box>
      }
    >
      <Box
        component="span"
        tabIndex={0}
        role="button"
        aria-label={`Info: ${info.title}`}
        data-testid={`info-${id}`}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 20,
          height: 20,
          borderRadius: "50%",
          bgcolor: CW.primary,
          color: "#fff",
          fontSize: 12,
          fontWeight: 800,
          fontFamily: "Georgia, serif",
          fontStyle: "italic",
          lineHeight: 1,
          cursor: "help",
          ml: 1,
          verticalAlign: "middle",
          flex: "0 0 auto",
          boxShadow: "0 0 0 3px rgba(11,159,174,0.18)",
          ...sx,
        }}
      >
        i
      </Box>
    </Tooltip>
  );
}

/** White card with optional title row (info = section id for InfoHint). */
export function Panel({ title, action, info, children, sx, ...rest }) {
  const infoOn = useInfoMode() && Boolean(info && SECTION_INFO[info]);
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: `1px solid ${CW.line}`,
        borderRadius: "14px",
        p: { xs: 2, md: 2.5 },
        minWidth: 0,
        boxSizing: "border-box",
        ...sx,
      }}
      {...rest}
    >
      {title || action || infoOn ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.5 }}>
          {title ? (
            <Typography sx={{ fontSize: { xs: 18, md: 20 }, fontWeight: 700, color: CW.ink }}>
              {title}
              {info ? <InfoHint id={info} /> : null}
            </Typography>
          ) : infoOn ? (
            <InfoHint id={info} sx={{ ml: 0 }} />
          ) : (
            <span />
          )}
          {action || null}
        </Box>
      ) : null}
      {children}
    </Box>
  );
}

/** Small label pill ("Coach only", "6 open", "Follow up"). */
export function Pill({ children, tone = "muted", sx }) {
  const tones = {
    muted: { bg: "#eef2f5", color: CW.muted },
    teal: { bg: CW.tealSoft, color: CW.primary },
    red: { bg: CW.redSoft, color: CW.red },
    amber: { bg: CW.amberSoft, color: CW.amber },
    green: { bg: CW.greenSoft, color: CW.green },
  };
  const t = tones[tone] || tones.muted;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1.25,
        py: 0.35,
        borderRadius: "999px",
        fontSize: 12,
        fontWeight: 700,
        bgcolor: t.bg,
        color: t.color,
        whiteSpace: "nowrap",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** Stat card (Reviews overdue / Ready for action …). */
export function StatCard({ label, value, hint, dot, valueColor, onClick, active, hideOnPhone }) {
  return (
    <Box
      component={onClick ? "button" : "div"}
      type={onClick ? "button" : undefined}
      onClick={onClick}
      sx={{
        font: "inherit",
        display: hideOnPhone ? { xs: "none", md: "block" } : "block",
        flex: "1 1 180px",
        minWidth: { xs: "calc(50% - 8px)", md: 0 },
        textAlign: "left",
        bgcolor: "#fff",
        border: `1.5px solid ${active ? CW.primary : CW.line}`,
        borderRadius: "14px",
        p: { xs: 1.5, md: 2 },
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: CW.ink }}>{label}</Typography>
        {dot ? <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: dot }} /> : null}
      </Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mt: 1 }}>
        <Typography sx={{ fontSize: { xs: 26, md: 30 }, fontWeight: 800, color: valueColor || CW.ink, lineHeight: 1 }}>
          {value}
        </Typography>
        {hint ? <Typography sx={{ fontSize: 12.5, color: CW.muted }}>{hint}</Typography> : null}
      </Box>
    </Box>
  );
}

export function Loading() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "45vh" }}>
      <CircularProgress size={34} />
    </Box>
  );
}

export function ErrorBox({ message, onRetry }) {
  return (
    <Box sx={{ p: 3, textAlign: "center" }}>
      <Typography sx={{ color: CW.red, mb: 1.5 }}>{message || "Something went wrong"}</Typography>
      {onRetry ? (
        <button type="button" className="small-contained-outlined-button" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </Box>
  );
}

/** Two-letter initials avatar. */
export function Initials({ name, size = 36 }) {
  const parts = String(name || "?").trim().split(/\s+/);
  const text = `${parts[0]?.[0] || ""}${parts.length > 1 ? parts[parts.length - 1][0] : ""}`.toUpperCase();
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: CW.tealSoft,
        color: CW.primary,
        fontWeight: 700,
        fontSize: size * 0.36,
        display: "grid",
        placeItems: "center",
        flex: "0 0 auto",
      }}
    >
      {text || "?"}
    </Box>
  );
}

/** Segmented tab buttons. */
export function Segments({ value, options, onChange, sx }) {
  return (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", ...sx }}>
      {options.map((opt) => {
        const on = opt.value === value;
        return (
          <Box
            key={opt.value}
            component="button"
            type="button"
            onClick={() => onChange(opt.value)}
            sx={{
        font: "inherit",
              border: `1px solid ${on ? CW.primary : CW.line}`,
              bgcolor: on ? CW.tealSoft : "#fff",
              color: on ? CW.primary : CW.ink,
              fontWeight: 600,
              fontSize: 13,
              px: 1.75,
              py: 0.9,
              borderRadius: "8px",
              cursor: "pointer",
            }}
          >
            {opt.label}
            {opt.count != null ? ` (${opt.count})` : ""}
          </Box>
        );
      })}
    </Box>
  );
}

/** Reads the coach user saved at login. */
export const currentUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user_data") || "{}") || {};
  } catch (_err) {
    return {};
  }
};

/** Underlined text tabs (design pages 1 / 2 / 9: "To review  Awaiting client …"). */
export function UnderlineTabs({ value, options, onChange, sx }) {
  // STAGE-CHECK (3 Nov): on a phone the tab row scrolls sideways; keep the
  // chosen tab in view (only the row scrolls, never the page).
  const rowRef = React.useRef(null);
  React.useEffect(() => {
    const row = rowRef.current;
    const on = row && row.querySelector('[aria-selected="true"]');
    if (!row || !on || row.scrollWidth <= row.clientWidth) return;
    const left = on.offsetLeft; // row is position:relative
    const right = left + on.offsetWidth;
    if (left < row.scrollLeft) row.scrollLeft = left;
    else if (right > row.scrollLeft + row.clientWidth) row.scrollLeft = right - row.clientWidth + 8;
  }, [value, options.length]);
  return (
    <Box
      ref={rowRef}
      role="tablist"
      sx={{
        position: "relative",
        display: "flex",
        gap: { xs: 2, md: 4 },
        borderBottom: `1px solid ${CW.line}`,
        overflowX: "auto",
        // scroll sideways on a phone, but without a visible scroll bar
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
        ...sx,
      }}
    >
      {options.map((opt) => {
        const on = opt.value === value;
        return (
          <Box
            key={opt.value}
            component="button"
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onChange(opt.value)}
            sx={{
              font: "inherit",
              border: 0,
              borderBottom: `3px solid ${on ? CW.primary : "transparent"}`,
              bgcolor: "transparent",
              color: on ? CW.primary : CW.ink,
              fontWeight: 600,
              fontSize: { xs: 14, md: 15.5 },
              px: 0,
              pt: 0.5,
              pb: 1.1,
              mb: "-1px",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {opt.label}
            {opt.count != null ? ` (${opt.count})` : ""}
          </Box>
        );
      })}
    </Box>
  );
}

/** Teal text action ("Open review", "View calendar →"). */
export function TextLink({ children, onClick, sx, ...rest }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        font: "inherit",
        border: 0,
        bgcolor: "transparent",
        p: 0,
        color: CW.primary,
        fontWeight: 700,
        fontSize: 14,
        cursor: "pointer",
        textAlign: "left",
        "&:hover": { textDecoration: "underline" },
        "&:disabled": { color: CW.muted, cursor: "default", textDecoration: "none" },
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}

/** Small uppercase label above a value ("SUBMITTED", "REPLY DUE"). */
export function Eyebrow({ children, sx }) {
  return (
    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: CW.muted, textTransform: "uppercase", ...sx }}>
      {children}
    </Typography>
  );
}

/** Teal breadcrumb trail (design: "‹ Back to queue / Clients / Jamie Reed / Review"). */
/**
 * HEAD-SIZE (3 Nov): every new screen's page title is a plain <h2>, so it
 * takes the portal theme's heading style (font, weight, colour) exactly
 * like the old pages (Members, Trackers, Automations = 30px). Only the
 * phone size and the margin are set here.
 */
export function PageTitle({ children, sx }) {
  return (
    <Box component="h2" sx={{ fontSize: { xs: 24, md: 30 }, lineHeight: 1.25, m: 0, ...sx }}>
      {children}
    </Box>
  );
}

export function Breadcrumbs({ items }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, fontSize: 14, mb: 1 }}>
      {items.map((it, i) => (
        <React.Fragment key={i}>
          {i ? <Box component="span" sx={{ color: CW.muted }}>/</Box> : null}
          {it.onClick ? (
            <TextLink onClick={it.onClick} sx={{ fontSize: 14 }}>
              {it.label}
            </TextLink>
          ) : (
            <Box component="span" sx={{ color: CW.primary, fontWeight: 700 }}>
              {it.label}
            </Box>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}

/**
 * ACTIONS-MENU (3 Nov): the "⋮" button for secondary actions (Login as
 * member, Review plan, Skip review …) so the header and strip stay clean.
 * items: [{ label, onClick, icon?, disabled?, hidden?, testId? }]
 */
export function ActionMenu({ items, label = "More actions", testId, sx }) {
  const [anchor, setAnchor] = React.useState(null);
  const shown = (items || []).filter((it) => it && !it.hidden);
  if (!shown.length) return null;
  return (
    <>
      <Tooltip title={label}>
        <IconButton
          size="small"
          aria-label={label}
          data-testid={testId}
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ color: CW.ink, border: `1px solid ${CW.line}`, bgcolor: "#fff", ...sx }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {shown.map((it) => (
          <MenuItem
            key={it.label}
            disabled={it.disabled}
            data-testid={it.testId}
            onClick={() => {
              setAnchor(null);
              if (it.onClick) it.onClick();
            }}
            sx={{ fontSize: 14, minWidth: 200 }}
          >
            {it.icon ? <ListItemIcon sx={{ minWidth: 32, color: CW.muted }}>{it.icon}</ListItemIcon> : null}
            {it.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/** HEADER-ICONS (3 Nov): small round icon button with a tooltip (e.g. Chat). */
export function IconAction({ title, onClick, children, testId, disabled, sx }) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          size="small"
          aria-label={title}
          data-testid={testId}
          disabled={disabled}
          onClick={onClick}
          sx={{ color: CW.primary, border: `1px solid ${CW.line}`, bgcolor: "#fff", ...sx }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}
